import type { ReactNode } from "react";
import { Link2, Unlink } from "lucide-react";
import { Button, Skeleton } from "@/design-system/components/ui";
import { DetailSection } from "../detailBits";
import { InlineQueryError } from "../queryStates";
import { PREQUAL_SUBJECT_COPY, type PrequalSubject } from "./prequalSubject";

/**
 * THE prequalification surface — one component, both subjects, every address.
 *
 * <p>The buyer's tab and the seller's tab shipped as separate implementations of
 * the same screen: a status block, a link-correction pair, a list of invitations
 * and a way to send one. They agreed on nothing but a noun and drifted on
 * everything else — which was four places to fix a rendering bug, two vocabularies
 * for one lifecycle, and two chances to get "a failed read is not an empty list"
 * wrong. This is that screen, once.</p>
 *
 * <p>🔴 <b>Parameterised on WHO IS BEING PREQUALIFIED, not on where you arrived
 * from.</b> The subject picks every word (see <c>prequalSubject</c>). The ADDRESS
 * — buyer, submission or listing — is carried by whichever adapter the mounting
 * surface built, and stays out of here entirely, because the address is what
 * decides the authorization gate: a submission-addressed write takes the buyer's
 * own per-row ownership rule and a listing-addressed one takes
 * <c>Listings.Manage</c> with no row check. Folding the two axes together would
 * let a change of surface silently change who may send.</p>
 *
 * <p><b>Slots, not branches.</b> The composer differs between subjects for a
 * recorded reason (a buyer's address is on file, a seller's must be typed and
 * therefore reviewed), and the buyer carries an attempts list the seller has no
 * equivalent of. Both arrive as children rather than as a flag this file switches
 * on, so adding a third subject needs no edit here.</p>
 *
 * <p>🔴 <b>CARDED, and the card is not decoration.</b> This surface rendered as a
 * bare stack of <c>&lt;section&gt;</c>s directly on the page background while
 * every sibling tab on the same record — and its own read-only twin on Overview,
 * <c>PrequalificationLinkPanel</c>, which prints these exact fields — went through
 * <c>DetailSection</c>. A reader moving between the two met the same facts in two
 * chrome vocabularies and had no way to tell whether they were looking at the same
 * feature. The heading is <c>copy.sectionTitle</c>, which had been declared and
 * documented on the subject record since the merge and never rendered.</p>
 */
export function PrequalificationSurface({
  subject,
  canEdit,
  status,
  link,
  invitations,
  intro,
  children,
  testId,
}: {
  subject: PrequalSubject;
  canEdit: boolean;

  /**
   * The current state of the link, as a query result plus what to print.
   *
   * <p>🔴 <b>Loading, FAILED and empty are three states, never two.</b> "Nothing
   * is linked" is a claim about the record, and a failed read is no evidence for
   * it — the worse direction of the two, because it sends a specialist off to
   * link one that is already there, or to invite someone who already has a live
   * invitation. Every prequalification surface in this module has shipped that
   * bug at least once.</p>
   */
  status: {
    loading: boolean;
    error: boolean;
    retrying: boolean;
    onRetry: () => void;
    /** Whether something is linked. Drives which of the two bodies renders. */
    linked: boolean;
    /** The badge and fields, when linked. */
    body: ReactNode;
  };

  /**
   * The link-correction pair. Omit entirely for a read-only surface.
   *
   * <p>Rendered together because they are one decision — the primary pointer is
   * either right, wrong, or absent — and separating them is how a surface ends up
   * offering Unlink with nothing to unlink. That is also why they stay in the
   * card's BODY rather than moving into its header action slot: the slot holds
   * one control, and splitting the pair across two places to fit it would
   * undo the reason they are a pair.</p>
   */
  link?: {
    onChange: () => void;
    onUnlink: () => void;
    unlinking: boolean;
    /** Copy under the pair. The buyer explains that unlinking clears the pointer
     *  and leaves the attempts standing; the seller has no attempts list. */
    footnote?: ReactNode;
  };

  /** The invitations block — a composed <c>InvitationsSection</c>. */
  invitations: ReactNode;

  /** Copy above everything, when the address needs to say something about
   *  itself. The submission-addressed seller surface uses it to state that this
   *  belongs to the listing and reaches every other buyer on it. */
  intro?: ReactNode;

  /** Extra sections below the invitations. The buyer supplies its attempts
   *  list; the seller supplies nothing. */
  children?: ReactNode;

  testId?: string;
}) {
  const copy = PREQUAL_SUBJECT_COPY[subject];

  return (
    <div data-testid={testId} className="grid gap-space-5">
      <DetailSection title={copy.sectionTitle}>
        <div className="grid gap-space-3">
          {intro}

          {status.loading ? (
            <Skeleton className="h-24 w-full" />
          ) : status.error ? (
            <InlineQueryError
              what={copy.linkWhat}
              onRetry={status.onRetry}
              retrying={status.retrying}
            />
          ) : status.linked ? (
            status.body
          ) : (
            <p
              className="m-0 text-small text-gray-mid"
              data-testid={`prequal-${subject}-unlinked`}
            >
              {copy.unlinkedCopy}
            </p>
          )}

          {/* Hidden entirely while the read is in flight or failed. Offering
              "Change the linked prequalification" over an unknown state invites a
              correction to something nobody has established. */}
          {canEdit && link && !status.loading && !status.error && (
            <div className="grid gap-space-2">
              <div className="flex flex-wrap gap-space-2">
                {/* SECONDARY — correcting the pointer is an alternative to the
                    section's primary act (sending an invitation), not a second
                    one competing with it. */}
                <Button variant="secondary" size="sm" onClick={link.onChange}>
                  <Link2 className="h-4 w-4" aria-hidden />
                  {status.linked ? copy.changeLinkAction : copy.linkExistingAction}
                </Button>

                {status.linked && (
                  // TEXT — the quiet half of the pair. Unlinking undoes rather
                  // than achieves, and it must never out-weigh the control that
                  // sets the pointer correctly.
                  <Button
                    variant="text"
                    size="sm"
                    disabled={link.unlinking}
                    onClick={link.onUnlink}
                  >
                    <Unlink className="h-4 w-4" aria-hidden />
                    Unlink
                  </Button>
                )}
              </div>
              {link.footnote && (
                <p className="m-0 text-caption text-gray-mid">{link.footnote}</p>
              )}
            </div>
          )}
        </div>
      </DetailSection>

      {invitations}

      {children}
    </div>
  );
}
